import React, { FC, useEffect, useRef, useState } from "react";
import { Navigation } from "@components/molecules/Navigation";
import Helmet from "react-helmet";
import { ThemeProvider } from "styled-components";
import { theme } from "@theme/theme";
import { GlobalStyles } from "@theme/GlobalStyles";
import { Loader } from "@components/molecules/Loader";
import { MainContext } from "@context";
import { Footer } from "@components/molecules/Footer";
import SweetScroll from "sweet-scroll";
import {
  ConsentModeBanner,
  defaultConsents,
  defaultHandlers,
} from "@rendpro/react-cookie-notice";
import "@rendpro/react-cookie-notice/styles-consent-mode-banner";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useI18next } from "gatsby-plugin-react-i18next";
import "photoswipe/dist/photoswipe.css";
import "photoswipe/dist/default-skin/default-skin.css";
import { usePageVisibility } from "react-page-visibility";
import Newsletter, {
  NewsletterContextProvider,
} from "@components/molecules/Newsletter";

export const BasicTemplate: FC<Props> = ({
  children,
  title,
  description,
  renderHero: Hero,
  isAnotherThanIndex,
  ogImage: propOgImage,
  withoutNewsletter,
}) => {
  const isDarkMode = false; // temp disabled dark mode

  const [isLoading, setLoading] = useState<boolean>(!isAnotherThanIndex);
  const [isLoaderExited, setLoaderExited] = useState<boolean>(
    !!isAnotherThanIndex
  );
  const [isOpen, setOpen] = useState<boolean>(false);
  const [scroller, setScroller] = useState<SweetScroll | null>(null);
  const { t, i18n } = useTranslation("common");
  const { t: cmbt } = useTranslation("consent-mode-banner");
  const { originalPath, language, languages, path } = useI18next();
  const ref = useRef<HTMLDivElement>(null);
  const isPageVisible = usePageVisibility();

  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.visibility = "";
  }, [ref]);

  useEffect(() => {
    if (isLoading) {
      document.body.classList.add("is-loading");
    } else {
      document.body.classList.remove("is-loading");
    }
  }, [isLoading]);

  useEffect(() => {
    setScroller(new SweetScroll({ duration: 2000, easing: "easeOutQuart" }));
  }, []);

  const switchTitle = () => {
    const newTitle = t("click-here");

    if (document.title === newTitle) {
      document.title = title;
    } else {
      document.title = newTitle;
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timer;

    if (!isPageVisible) {
      interval = setInterval(switchTitle, 1500);
    } else {
      document.title = title;
    }

    return () => {
      clearInterval(interval);
    };
  }, [isPageVisible]);

  const currTheme = isDarkMode ? theme.dark : theme.light;

  return (
    <div style={{ visibility: "hidden" }} ref={ref}>
      <ThemeProvider theme={currTheme}>
        <MainContext.Provider
          value={{
            isLoading,
            isLoaderExited,
            setLoading,
            setLoaderExited,
            scroller,
            isOpen,
            setOpen,
          }}
        >
          <GlobalStyles />
          <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta
              property="og:url"
              content={`https://rend.pro${
                path.startsWith("/") ? path : `/${path}`
              }`}
            />
            <meta property="og:type" content="website" />
            <meta property="og:locale" content={i18n.language} />
            <meta property="og:image" content={propOgImage || "/ogimage.jpg"} />
            <link
              rel="canonical"
              href={`https://rend.pro${
                path.startsWith("/") ? path : `/${path}`
              }`}
            />
            <html lang={i18n.language} />
            {languages.map((lng) => {
              if (lng === language) return;

              let href = "https://rend.pro/";

              if (lng !== "pl") {
                href += lng + "/";
              }

              href += originalPath.startsWith("/")
                ? originalPath.slice(1)
                : originalPath;

              if (href.endsWith("//")) {
                href = href.slice(0, -1);
              }

              return (
                <link rel="alternate" hrefLang={lng} href={href} key={lng} />
              );
            })}
            <meta
              name="google-site-verification"
              content="yKO9Hl6wsp4V138stz0XyRiPYRVeM2CFAfMG4DYK-VI"
            />
          </Helmet>
          <header>
            <Navigation isDarkMode={isDarkMode} toggleDarkMode={() => {}} />
            {Hero && <Hero />}
          </header>
          <main>{children}</main>
          <NewsletterContextProvider>
            {!withoutNewsletter ? (
              <>
                <Newsletter />
                <Footer />
              </>
            ) : (
              <Footer withoutNewsletter={withoutNewsletter} />
            )}
          </NewsletterContextProvider>

          <Loader
            isLoading={isLoading}
            setLoaderExited={setLoaderExited}
            setLoading={setLoading}
            isLoaderExited={isLoaderExited}
          />
          <ConsentModeBanner
            title={cmbt("title")}
            subtitle={cmbt("subtitle")}
            description={
              <span dangerouslySetInnerHTML={{ __html: cmbt("description") }} />
            }
            color="#F29122"
            denyAllLabel={cmbt("denyAllLabel")}
            grantAllLabel={cmbt("grantAllLabel")}
            saveLabel={cmbt("saveLabel")}
            {...defaultConsents({
              essential: cmbt("essential"),
              preferences: cmbt("preferences"),
              statistics: cmbt("statistics"),
              marketing: cmbt("marketing"),
            })}
            {...defaultHandlers()}
          />
        </MainContext.Provider>
      </ThemeProvider>
    </div>
  );
};

interface Props {
  title: string;
  description: string;
  renderHero?: FC;
  isAnotherThanIndex?: boolean;
  ogImage?: string;
  withoutNewsletter?: boolean;
}
